import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';
import { getDataAPI, postDataAPI } from '../../utils/fetchData';

const FlightSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { modalConfig, showModal, closeModal } = useModal();
  
  const [shows, setShows] = useState([]);
  const [selectedShows, setSelectedShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  
  // Tour plan context
  const [tourPlanId, setTourPlanId] = useState(null);
  const [tourPlanInfo, setTourPlanInfo] = useState(null);

  // Search criteria
  const [origin, setOrigin] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [cabinClass, setCabinClass] = useState('economy');
  const [passengers, setPassengers] = useState(1);
  
  // Tour settings for smart defaults
  const [tourSettings, setTourSettings] = useState(null);

  useEffect(() => {
    // Load tour settings first
    loadTourSettings();
    
    // Pre-fill home city from ViewAll if provided
    if (location.state?.homeCity) {
      setOrigin(location.state.homeCity);
    }
    
    // If coming from ViewAll with pre-selected shows - set them immediately
    if (location.state?.selectedShows && location.state.selectedShows.length > 0) {
      const preSelectedShows = location.state.selectedShows;
      
      // Check if they're already full objects or just IDs
      if (preSelectedShows[0]?._id) {
        // Already full show objects - set them now
        console.log('🎯 Setting pre-selected shows:', preSelectedShows);
        setSelectedShows(preSelectedShows);
        calculateSmartDepartureDate(preSelectedShows);
      }
    }
    
    // Check if coming from a tour plan
    if (location.state?.tourPlanId) {
      setTourPlanId(location.state.tourPlanId);
      loadTourPlanContext(location.state.tourPlanId).then(() => {
        // Load shows after tour plan context is loaded
        loadUpcomingShows();
      });
    } else {
      // No tour plan, just load shows normally
      loadUpcomingShows();
    }
  }, []);
  
  // Reload shows when tour plan info changes
  useEffect(() => {
    if (tourPlanInfo) {
      loadUpcomingShows();
    }
  }, [tourPlanInfo]);
  
  // Recalculate departure date when shows change
  useEffect(() => {
    if (selectedShows.length > 0) {
      calculateSmartDepartureDate(selectedShows);
    }
  }, [selectedShows, tourSettings]);
  
  const loadTourSettings = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/settings', token);
      
      if (response.data.status === 200 && response.data.data.settings) {
        setTourSettings(response.data.data.settings);
      }
    } catch (error) {
      console.error('Error loading tour settings:', error);
    }
  };
  
  const calculateSmartDepartureDate = (shows) => {
    if (!shows || shows.length === 0 || !tourSettings) return;
    
    // Find the earliest show
    const sortedShows = [...shows].sort((a, b) => 
      new Date(a.showDate) - new Date(b.showDate)
    );
    const firstShow = sortedShows[0];
    const showDate = new Date(firstShow.showDate);
    
    // Extract show time (if available)
    let showHour = 20; // Default to 8pm if no time specified
    if (firstShow.showInfo?.showtimes) {
      const timeMatch = firstShow.showInfo.showtimes.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)/i);
      if (timeMatch) {
        let hour = parseInt(timeMatch[1]);
        const period = timeMatch[3].toLowerCase();
        if (period === 'pm' && hour !== 12) hour += 12;
        if (period === 'am' && hour === 12) hour = 0;
        showHour = hour;
      }
    }
    
    const cutoffHour = tourSettings.flightDepartureTimeCutoff || 18;
    
    // If show is after cutoff time, fly in the day before
    let flightDate;
    if (showHour >= cutoffHour) {
      flightDate = new Date(showDate);
      flightDate.setDate(flightDate.getDate() - 1);
      console.log(`🛫 Show at ${showHour}:00 is after ${cutoffHour}:00 cutoff - flying in day before`);
    } else {
      flightDate = showDate;
      console.log(`🛫 Show at ${showHour}:00 is before ${cutoffHour}:00 cutoff - flying same day`);
    }
    
    // Format as YYYY-MM-DD
    const formattedDate = flightDate.toISOString().split('T')[0];
    setDepartureDate(formattedDate);
    console.log(`📅 Smart departure date set to: ${formattedDate}`);
  };
  
  const loadTourPlanContext = async (planId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI(`user/tm/tour-plans/${planId}`, token);
      
      if (response.data.status === 200) {
        const plan = response.data.data.tourPlan;
        setTourPlanInfo({
          name: plan.name,
          homeCity: plan.homeCity,
          flights: plan.flights,
          showIds: plan.shows.map(show => show._id || show) // Extract show IDs
        });
        
        // Pre-fill origin with home city if available
        if (plan.homeCity && !origin) {
          setOrigin(plan.homeCity);
        }
      }
    } catch (error) {
      console.error('Error loading tour plan context:', error);
    }
  };

  const sortFlightsByPreference = (flights) => {
    if (!tourSettings?.preferredAirlines || Object.keys(tourSettings.preferredAirlines).length === 0) {
      return flights; // No preferences, return as-is
    }
    
    // preferredAirlines is now { "Delta": 1, "United": 2, ... }
    const rankings = tourSettings.preferredAirlines;
    
    return [...flights].sort((a, b) => {
      const aAirline = (a.airline?.name || a.airline || '').toLowerCase();
      const bAirline = (b.airline?.name || b.airline || '').toLowerCase();
      
      // Find matching rank for each flight
      let aRank = 999; // High number = low priority
      let bRank = 999;
      
      Object.keys(rankings).forEach(airline => {
        if (aAirline.includes(airline.toLowerCase())) {
          aRank = Math.min(aRank, rankings[airline]); // Use best matching rank
        }
        if (bAirline.includes(airline.toLowerCase())) {
          bRank = Math.min(bRank, rankings[airline]);
        }
      });
      
      // Sort by rank (lower number = higher priority)
      return aRank - bRank;
    });
  };
  
  const loadUpcomingShows = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/shows', token);
      
      console.log('FlightSearch - API Response:', response.data);
      
      if (response.data.status === 200 && response.data.data) {
        const allShows = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        
        let upcomingShows = allShows.filter(show => 
          !['completed', 'cancelled'].includes(show.status)
        );
        
        // If we have a tour plan, filter to only shows in that plan
        if (tourPlanInfo?.showIds) {
          console.log('Filtering shows for tour plan:', tourPlanInfo.showIds);
          upcomingShows = upcomingShows.filter(show => 
            tourPlanInfo.showIds.includes(show._id)
          );
          console.log('Filtered shows:', upcomingShows);
        }
        
        console.log('FlightSearch - Upcoming shows:', upcomingShows);
        setShows(upcomingShows);
        
        // Auto-select first show ONLY if:
        // 1. No shows currently selected
        // 2. No pre-selected shows were passed via location.state
        const hasPreSelectedShows = location.state?.selectedShows && location.state.selectedShows.length > 0;
        if (selectedShows.length === 0 && upcomingShows.length > 0 && !hasPreSelectedShows) {
          console.log('🔄 Auto-selecting first show (no pre-selection)');
          setSelectedShows([upcomingShows[0]]);
        } else if (hasPreSelectedShows && selectedShows.length > 0) {
          console.log('✅ Keeping pre-selected shows:', selectedShows.length);
        }
      }
    } catch (error) {
      console.error('Error loading shows:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (selectedShows.length === 0) {
      showModal('No Shows Selected', 'Please select at least one show to search flights for.', 'warning');
      return;
    }

    if (!origin) {
      showModal('Origin Required', 'Please enter a departure city or airport code.', 'warning');
      return;
    }

    try {
      setSearching(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));

      // For multiple shows, we'll search flights to each destination
      const allFlights = [];
      const errors = [];
      
      for (const show of selectedShows) {
        const showCity = show.venue?.city || '';
        
        // Skip shows with invalid cities
        if (!showCity || showCity.toLowerCase() === 'unknown' || showCity.toLowerCase() === 'tbd' || showCity.toLowerCase().includes('unknown')) {
          console.log(`⚠️ Skipping show "${show.venue?.name}" - invalid city: ${showCity}`);
          errors.push({
            show: show.venue?.name || 'Unknown Venue',
            reason: 'Invalid or missing city'
          });
          continue;
        }
        
        const searchParams = {
          show_id: show._id,
          origin: origin,
          destination: showCity,
          departure_date: departureDate || show.showDate,
          cabin_class: cabinClass,
          passengers: passengers
        };

        console.log(`🌐 Searching flights for show: ${show.venue?.name || 'Unknown'}`);
        console.log('Search params:', searchParams);

        try {
          const response = await postDataAPI('user/tm/flights/search', searchParams, token);
          
          if (response.data.status === 200 && response.data.data) {
            const flights = response.data.data.flights || [];
            // Add show info to each flight so we know which show it's for
            const flightsWithShowInfo = flights.map(f => ({
              ...f,
              showId: show._id,
              showName: show.venue?.name || 'Unknown Venue',
              showCity: show.venue?.city || 'Unknown',
              showDate: show.showDate
            }));
            allFlights.push(...flightsWithShowInfo);
          } else if (response.data.status === 400) {
            // Handle validation errors
            console.log(`⚠️ Validation error for show "${show.venue?.name}": ${response.data.message}`);
            errors.push({
              show: show.venue?.name || 'Unknown Venue',
              reason: response.data.message || 'Invalid data'
            });
          }
        } catch (showError) {
          console.error(`Error searching flights for show ${show.venue?.name}:`, showError);
          errors.push({
            show: show.venue?.name || 'Unknown Venue',
            reason: 'Search failed'
          });
        }
      }
      
      console.log(`✅ Found ${allFlights.length} total flights for ${selectedShows.length} shows`);
      
      // Sort flights by preferred airlines
      const sortedFlights = sortFlightsByPreference(allFlights);
      setSearchResults(sortedFlights);
      
      if (errors.length > 0) {
        const errorMessage = errors.map(e => `• ${e.show}: ${e.reason}`).join('\n');
        showModal(
          'Search Partially Complete',
          `Found ${allFlights.length} flights, but ${errors.length} show(s) had issues:\n\n${errorMessage}\n\nPlease update the venue cities for these shows.`,
          'warning'
        );
      } else if (allFlights.length === 0) {
        showModal('No Flights Found', 'No flights found for your search criteria. Try adjusting your dates or cabin class.', 'info');
      } else {
        showModal('Search Complete', `Found ${allFlights.length} flight options for ${selectedShows.length} show(s) via web search.`, 'success');
      }
    } catch (error) {
      console.error('Error searching flights:', error);
      showModal('Search Error', 'Failed to search flights. Please try again.', 'error');
    } finally {
      setSearching(false);
    }
  };

  const handleAddFlight = async (flight) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      console.log('🎫 Adding flight to show:', flight.showName);
      console.log('Flight data being sent:', flight);
      
      const flightData = {
        show_id: flight.showId, // Use the showId attached to the flight
        ...flight
      };

      const response = await postDataAPI('user/tm/flights', flightData, token);
      
      console.log('Add flight response:', response.data);
      
      if (response.data.status === 200 || response.data.status === 201) {
        showModal('Flight Added', `Flight has been added to ${flight.showName}!`, 'success');
        // Don't auto-navigate - let user continue searching or use Save & Close button
      } else {
        console.error('Unexpected response status:', response.data);
        showModal('Error', response.data.message || 'Failed to add flight. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error adding flight:', error);
      console.error('Error details:', error.response?.data || error.message);
      showModal('Error', error.response?.data?.message || 'Failed to add flight. Please try again.', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateBadge = (dateString) => {
    if (!dateString) return { month: '', day: '' };
    const date = new Date(dateString);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.getDate()
    };
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = timeString.includes('T') ? new Date(timeString) : new Date(`2000-01-01T${timeString}`);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return timeString;
    }
  };

  const getAirlineIcon = (airline) => {
    const icons = {
      'Delta': '🔺',
      'United': '🔵',
      'American': '🔴',
      'JetBlue': '🔷',
      'Southwest': '❤️'
    };
    return icons[airline] || '✈️';
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <div className="flex flex-col gap-1">
              <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Flight Search</span>
              {tourPlanInfo && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 bg-[rgba(255,149,0,0.2)] text-[#FF9500] rounded border border-[rgba(255,149,0,0.3)]">
                    📋 {tourPlanInfo.name}
                  </span>
                  <button
                    onClick={() => navigate(`/tour-manager/tour-overview/${tourPlanId}`)}
                    className="text-[10px] text-[rgba(255,255,255,0.5)] hover:text-white underline"
                  >
                    View Plan
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (tourPlanId) {
                    navigate(`/tour-manager/tour-overview/${tourPlanId}`, {
                      state: { fromFlightSearch: true }
                    });
                  } else {
                    navigate('/tour-manager/view-all');
                  }
                }}
                className="px-3 py-1.5 bg-[#FF9500] text-white text-[12px] rounded hover:bg-[#FF7700] transition-colors"
              >
                Save & Close
              </button>
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Back
              </span>
            </div>
          </div>

          {/* Tour Context Banner */}
          {tourPlanId && selectedShows.length > 1 && (
            <div className="w-full px-4 mb-3">
              <div className="bg-[rgba(147,51,234,0.1)] border border-[rgba(147,51,234,0.3)] rounded-lg p-3 text-center">
                <span className="text-purple-400 text-[11px]">
                  🗺️ Booking travel for your tour ({selectedShows.length} shows)
                </span>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {loading ? (
              <div className="text-center py-[60px]">
                <p className="text-white text-[12px]">Loading shows...</p>
              </div>
            ) : shows.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-semibold mb-2">
                  No upcoming shows
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  You need upcoming shows to search for flights. Add shows first.
                </p>
              </div>
            ) : (
              <>
                {/* Show Selection - Multiple with checkboxes */}
                <div className="mb-6">
                  <label className="text-white text-[12px] font-semibold mb-3 block">
                    Select Shows ({selectedShows.length} selected)
                  </label>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto bg-[rgba(255,255,255,0.03)] rounded-lg p-3 border border-[rgba(255,255,255,0.1)]">
                    {shows.map(show => {
                      const isSelected = selectedShows.some(s => s._id === show._id);
                      return (
                        <div
                          key={show._id}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedShows(selectedShows.filter(s => s._id !== show._id));
                            } else {
                              setSelectedShows([...selectedShows, show]);
                            }
                          }}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'bg-[rgba(255,149,0,0.2)] border border-[#FF9500]' 
                              : 'bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,149,0,0.5)]'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-[#FF9500] border-[#FF9500]' : 'border-[rgba(255,255,255,0.3)]'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-[12px] font-semibold truncate">
                              {show.venue?.name || 'Unknown Venue'}
                            </p>
                            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                              {show.venue?.city || 'Unknown City'}{show.venue?.state ? `, ${show.venue.state}` : ''} • {formatDate(show.showDate)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {selectedShows.length > 1 && (
                    <p className="text-[rgba(255,149,0,0.8)] text-[10px] mt-2">
                      ✈️ Searching flights for multi-show tour
                    </p>
                  )}
                </div>

                {/* Search Form */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-white text-[12px] font-semibold mb-2 block">
                      From (Origin Airport)
                    </label>
                    <input
                      type="text"
                      value={origin}
                      onChange={(e) => setOrigin(e.target.value)}
                      placeholder="LAX, JFK, ORD, or city name"
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-4 py-3 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500]"
                    />
                    <p className="text-[rgba(255,255,255,0.4)] text-[10px] mt-1">
                      Enter airport code (LAX) or city name (Los Angeles)
                    </p>
                  </div>

                  <div>
                    <label className="text-white text-[12px] font-semibold mb-2 block">
                      Departure Date
                    </label>
                    <input
                      type="date"
                      value={departureDate}
                      onChange={(e) => setDepartureDate(e.target.value)}
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-4 py-3 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white text-[12px] font-semibold mb-2 block">
                        Cabin Class
                      </label>
                      <select
                        value={cabinClass}
                        onChange={(e) => setCabinClass(e.target.value)}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-4 py-3 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                      >
                        <option value="economy">Economy</option>
                        <option value="premium_economy">Premium</option>
                        <option value="business">Business</option>
                        <option value="first">First</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-white text-[12px] font-semibold mb-2 block">
                        Passengers
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="9"
                        value={passengers}
                        onChange={(e) => setPassengers(parseInt(e.target.value))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-4 py-3 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSearch}
                    disabled={searching}
                    className="w-full bg-[#FF9500] text-white border-none rounded-lg px-5 py-3 text-[12px] font-semibold cursor-pointer transition-colors hover:bg-[#FF8800] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {searching ? '🌐 Searching Web...' : '🔍 Search Flights (Web Search)'}
                  </button>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white text-[12px] font-semibold">
                        Flight Options Found ({searchResults.length})
                      </h3>
                      <span className="text-[rgba(255,255,255,0.5)] text-[10px]">
                        🌐 Web Search
                      </span>
                    </div>
                    <div className="space-y-3 pb-6">
                      {searchResults.map((flight, index) => {
                        const dateBadge = formatDateBadge(flight.departure_time || flight.showDate);
                        return (
                          <div
                            key={index}
                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg overflow-hidden hover:border-[#FF9500] transition-all"
                          >
                            <div className="flex">
                              {/* Date Badge - Left Side */}
                              <div className="bg-[rgba(0,0,0,0.3)] px-4 py-4 flex flex-col items-center justify-center min-w-[70px] border-r border-[rgba(255,255,255,0.1)]">
                                <p className="text-[#FFD60A] text-[11px] font-bold tracking-wide">
                                  {dateBadge.month}
                                </p>
                                <p className="text-white text-[24px] font-bold leading-none mt-1">
                                  {dateBadge.day}
                                </p>
                              </div>

                              {/* Flight Info - Right Side */}
                              <div className="flex-1 p-4">
                                {/* Show info tag */}
                                {flight.showName && (
                                  <div className="mb-3 pb-3 border-b border-[rgba(255,255,255,0.1)]">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-[rgba(255,149,0,0.9)] text-[10px] font-semibold">
                                          🎵 {flight.showName}
                                        </p>
                                        <p className="text-[rgba(255,255,255,0.5)] text-[9px]">
                                          {flight.showCity} • {formatDate(flight.showDate)}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[16px]">{getAirlineIcon(flight.airline)}</span>
                                    <span className="text-white text-[12px] font-semibold">
                                      {flight.origin} → {flight.destination}
                                    </span>
                                  </div>
                                  {flight.price && (
                                    <span className="text-[#34C759] text-[12px] font-semibold">
                                      ${flight.price}
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <p className="text-white text-[14px] font-semibold">
                                      {formatTime(flight.departure_time)}
                                    </p>
                                    <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                      {flight.origin}
                                    </p>
                                  </div>
                                  <div className="flex-1 mx-4 text-center">
                                    <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                      {flight.flight_duration || '2h 30m'}
                                    </p>
                                    <div className="h-[1px] bg-[rgba(255,255,255,0.2)] my-1"></div>
                                    <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                      {flight.stops === 0 ? 'Nonstop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-white text-[14px] font-semibold">
                                      {formatTime(flight.arrival_time)}
                                    </p>
                                    <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                      {flight.destination}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between">
                                  <div className="text-[10px] text-[rgba(255,255,255,0.6)]">
                                    {flight.airline} {flight.flight_number && `• ${flight.flight_number}`}
                                    {flight.source && (
                                      <span className="ml-2 text-[rgba(255,255,255,0.4)]">
                                        • {flight.source}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    {flight.booking_url && (
                                      <a
                                        href={flight.booking_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-[rgba(52,199,89,0.2)] text-[#34C759] border-none rounded-lg px-3 py-2 text-[10px] font-semibold cursor-pointer transition-colors hover:bg-[rgba(52,199,89,0.3)] no-underline"
                                      >
                                        View on Google
                                      </a>
                                    )}
                                    <button
                                      onClick={() => handleAddFlight(flight)}
                                      className="bg-[rgba(255,149,0,0.2)] text-[#FF9500] border-none rounded-lg px-4 py-2 text-[10px] font-semibold cursor-pointer transition-colors hover:bg-[rgba(255,149,0,0.3)]"
                                    >
                                      Add Flight
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Custom Modal */}
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </LayoutHeader>
  );
};

export default FlightSearch;

